import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { MESSAGE_HIGHLIGHT_DURATION_MS } from './AskSession.constants';

export type UseAskSessionNavigationHandlersParams = {
  setPinnedMessageIds: Dispatch<SetStateAction<Set<string>>>;
  setHighlightedMessageId: Dispatch<
    SetStateAction<string | undefined>
  >;
  searchMatches: string[];
  setSearchMatchIndex: Dispatch<SetStateAction<number>>;
  setExpandedSynopsis: Dispatch<
    SetStateAction<Record<string, boolean>>
  >;
};

export type UseAskSessionNavigationHandlersResult = {
  togglePinMessage: (messageId: string) => void;
  handleMessageClick: (messageId: string) => void;
  handlePinMessage: (messageId: string) => void;
  navigateSearchMatch: (direction: 'next' | 'prev') => void;
  toggleSynopsis: (docId: string) => void;
};

export const useAskSessionNavigationHandlers = ({
  setPinnedMessageIds,
  setHighlightedMessageId,
  searchMatches,
  setSearchMatchIndex,
  setExpandedSynopsis,
}: UseAskSessionNavigationHandlersParams): UseAskSessionNavigationHandlersResult => {
  const togglePinMessage = useCallback(
    (messageId: string) => {
      setPinnedMessageIds((prev) => {
        const next = new Set(prev);
        if (next.has(messageId)) {
          next.delete(messageId);
        } else {
          next.add(messageId);
        }
        return next;
      });
    },
    [setPinnedMessageIds],
  );

  const handleMessageClick = useCallback(
    (messageId: string) => {
      setHighlightedMessageId(messageId);
      setTimeout(
        () => setHighlightedMessageId(undefined),
        MESSAGE_HIGHLIGHT_DURATION_MS,
      );
    },
    [setHighlightedMessageId],
  );

  const handlePinMessage = useCallback(
    (messageId: string) => {
      togglePinMessage(messageId);
    },
    [togglePinMessage],
  );

  const navigateSearchMatch = useCallback(
    (direction: 'next' | 'prev') => {
      if (searchMatches.length === 0) return;

      setSearchMatchIndex((prevIndex) => {
        const newIndex =
          direction === 'next'
            ? (prevIndex + 1) % searchMatches.length
            : (prevIndex - 1 + searchMatches.length) % searchMatches.length;
        setHighlightedMessageId(searchMatches[newIndex]);
        return newIndex;
      });
    },
    [searchMatches, setSearchMatchIndex, setHighlightedMessageId],
  );

  const toggleSynopsis = useCallback(
    (docId: string) => {
      setExpandedSynopsis((prev) => ({
        ...prev,
        [docId]: !prev[docId],
      }));
    },
    [setExpandedSynopsis],
  );

  return {
    togglePinMessage,
    handleMessageClick,
    handlePinMessage,
    navigateSearchMatch,
    toggleSynopsis,
  };
};

